import React, { useEffect, useState } from "react";
import {
  MinusCircleOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import { Button, Form, Image, Input, InputNumber, Switch, Tooltip } from "antd";
import IconSelector from "./IconSelector";

const RewardForm = ({
  form,
  selectedLayout,
  hasWelcomeReward,
  setRewardsAssets,
}) => {
  const [allowedValues, setAllowedValues] = useState([]);

  const getFirstRewardValue = () => {
    if (selectedLayout === "layout1") return 5;
    if (selectedLayout === "layout2") return 8;
    return 1;
  };

  const getAllowedValues = () => {
    if (selectedLayout === "layout1") return [1, 2, 3];
    if (selectedLayout === "layout2") return [1, 2, 3, 4, 5, 6];
    return [];
  };

  const updateAllowedAndFirstReward = () => {
    const rewards = form.getFieldValue("rewards") || [];
    const firstValue = getFirstRewardValue();
    const updatedRewards = [...(rewards.length ? rewards : [{}])];
    const currentFirst = updatedRewards[0] || {};

    if (currentFirst.qualifyingPurchases !== firstValue) {
      updatedRewards[0] = {
        ...currentFirst,
        qualifyingPurchases: firstValue,
      };
      form.setFieldsValue({ rewards: updatedRewards });
    }

    setAllowedValues(getAllowedValues());
  };

  useEffect(() => {
    updateAllowedAndFirstReward();
  }, [selectedLayout, hasWelcomeReward]);

  return (
    <div className="rewards-form rewards-form-section">
      <div className="rewards-form-title">
        <h4>Rewards</h4>
        <Tooltip
          title={
            <Image
              src="/assets/images/stampcard/stamp-hint.png"
              alt="Hint"
              style={{ maxWidth: 300 }}
            />
          }
        >
          <QuestionCircleOutlined className="tooltip-icon" />
        </Tooltip>
      </div>

      <div
        className="rewards-form"
        name="dynamic_form_rewards"
        autoComplete="off"
      >
        <Form.List name="rewards">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }, index) => {
                const isFirst = index === 0;
                const firstValue = getFirstRewardValue();

                return (
                  <div key={key}>
                    <div className="form-row margin-y">
                      <Form.Item
                        {...restField}
                        name={[name, "title"]}
                        label="Title"
                        rules={[{ required: true, message: "Missing title" }]}
                      >
                        <Input />
                      </Form.Item>
                    </div>

                    {/* 🎯 Icon Selector  */}
                    <div className="form-row margin-y">
                      <Form.Item label="Icons" required>
                        <IconSelector
                          onChange={({ target, inking }) => {
                            const rewards = form.getFieldValue("rewards") || [];
                            const updated = [...rewards];
                            updated[name] = {
                              ...updated[name],
                              inkingIcon: inking,
                              targetIcon: target,
                            };
                            form.setFieldsValue({ rewards: updated });
                            setRewardsAssets(updated);
                          }}
                        />
                      </Form.Item>

                      <Form.Item
                        {...restField}
                        name={[name, "inkingIcon"]}
                        style={{ display: "none" }}
                      >
                        <Input />
                      </Form.Item>

                      <Form.Item
                        {...restField}
                        name={[name, "targetIcon"]}
                        style={{ display: "none" }}
                      >
                        <Input />
                      </Form.Item>
                    </div>

                    <div className="form-row margin-y">
                      <Form.Item
                        {...restField}
                        name={[name, "qualifyingPurchases"]}
                        label="Qualifying Purchases"
                        rules={[
                          {
                            required: true,
                            message: "Enter required purchases",
                          },
                          {
                            validator: (_, value) => {
                              if (isFirst) {
                                return Promise.resolve(); // First reward is read-only
                              }
                              if (!value) {
                                return Promise.reject(
                                  new Error("Please enter a value")
                                );
                              }
                              if (!allowedValues.includes(value)) {
                                return Promise.reject(
                                  new Error(
                                    `Must be one of: ${allowedValues.join(
                                      ", "
                                    )}`
                                  )
                                );
                              }
                              const first = getFirstRewardValue();
                              if (
                                [first, first - 1, first + 1].includes(value)
                              ) {
                                return Promise.reject(
                                  new Error(
                                    `Cannot be ${first}, ${first - 1}, or ${
                                      first + 1
                                    } (first reward or adjacent)`
                                  )
                                );
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <InputNumber
                          readOnly={isFirst}
                          style={isFirst ? { backgroundColor: "#f5f5f5" } : {}}
                          min={Math.min(...allowedValues)}
                          max={Math.max(...allowedValues)}
                          placeholder={
                            isFirst
                              ? `${firstValue}`
                              : `Allowed: ${allowedValues.join(", ")}`
                          }
                          onKeyPress={(e) => {
                            const char = e.key;
                            if (
                              [
                                "Backspace",
                                "Tab",
                                "ArrowLeft",
                                "ArrowRight",
                              ].includes(char)
                            ) {
                              return;
                            }
                            const next = parseInt(e.currentTarget.value + char);
                            if (isNaN(next) || !allowedValues.includes(next)) {
                              e.preventDefault();
                            }
                          }}
                          onPaste={(e) => {
                            const pasted = e.clipboardData.getData("Text");
                            const pastedValue = parseInt(pasted);
                            if (
                              isNaN(pastedValue) ||
                              !allowedValues.includes(pastedValue)
                            ) {
                              e.preventDefault();
                            }
                          }}
                        />
                      </Form.Item>

                      <Form.Item
                        {...restField}
                        name={[name, "confirmationTitle"]}
                        label="Confirmation Title"
                        rules={[
                          {
                            required: true,
                            message: "Missing confirmation title",
                          },
                        ]}
                      >
                        <Input />
                      </Form.Item>
                    </div>

                    <div className="form-row margin-y">
                      <Form.Item
                        {...restField}
                        name={[name, "confirmationMessage"]}
                        label="Confirmation Message"
                        rules={[
                          {
                            required: true,
                            message: "Missing confirmation message",
                          },
                        ]}
                      >
                        <Input />
                      </Form.Item>

                      <Form.Item
                        {...restField}
                        name={[name, "deferrable"]}
                        label="Deferrable"
                        valuePropName="checked"
                      >
                        <Switch checkedChildren="Yes" unCheckedChildren="No" />
                      </Form.Item>
                    </div>

                    <div className="form-row margin-y">
                      <Form.Item
                        {...restField}
                        name={[name, "voucherDurationDays"]}
                        label="Voucher Duration (Days)"
                        rules={[
                          { required: true, message: "Enter voucher duration" },
                        ]}
                      >
                        <InputNumber min={1} step={1} precision={0} />
                      </Form.Item>

                      <Form.Item
                        {...restField}
                        name={[name, "description"]}
                        label="Description"
                        rules={[
                          { required: true, message: "Missing description" },
                        ]}
                      >
                        <Input />
                      </Form.Item>
                    </div>

                    <div className="form-row margin-y">
                      <Form.Item
                        {...restField}
                        name={[name, "terms"]}
                        label="Terms"
                        rules={[{ required: true, message: "Missing terms" }]}
                      >
                        <Input />
                      </Form.Item>
                    </div>

                    <div className="form-row margin-y">
                      <Form.Item>
                        <Button
                          type="dashed"
                          onClick={() => remove(name)}
                          block
                          icon={<MinusCircleOutlined />}
                          disabled={fields.length === 1}
                        >
                          Remove
                        </Button>
                      </Form.Item>
                    </div>
                  </div>
                );
              })}

              {fields.length < 2 && (
                <div className="form-row margin-y">
                  <Button
                    type="primary"
                    onClick={() => {
                      const newReward = {
                        qualifyingPurchases:
                          allowedValues.find(
                            (val) =>
                              val !== getFirstRewardValue() &&
                              ![
                                getFirstRewardValue() - 1,
                                getFirstRewardValue() + 1,
                              ].includes(val)
                          ) || allowedValues[0], // Pick a valid default
                        title: "",
                        confirmationTitle: "",
                        confirmationMessage: "",
                        deferrable: false,
                        voucherDurationDays: 1,
                        description: "",
                        terms: "",
                      };
                      add(newReward);
                    }}
                    block
                    icon={<PlusOutlined />}
                  >
                    Add Reward
                  </Button>
                </div>
              )}

              {fields.length >= 2 && (
                <div className="form-row margin-y">
                  <p style={{ textAlign: "center", color: "#888" }}>
                    Maximum of 2 rewards allowed
                  </p>
                </div>
              )}
            </>
          )}
        </Form.List>
      </div>
    </div>
  );
};

export default RewardForm;
